import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError, isNetworkError, isForbiddenError } from '../../util/errors';
import { SharetribeApi } from '../../util/api';
import * as log from '../../util/log';
import { normalizeUsername } from '../../util/username';
import { setCurrentUser } from '../../ducks/user.duck';
import { StatusCode } from '../../util/enums';
import {
  createSignupNameValidationApiError,
  validateSignupNameFields,
} from '../../util/signupNameValidation';

// ================ Async Thunks ================ //

//////////////////
// Upload Image //
//////////////////
export const uploadImageThunk = createAsyncThunk(
  'ProfileSettingsPage/uploadImage',
  ({ id, file }, { rejectWithValue, extra: sdk }) => {
    const bodyParams = {
      image: file,
    };
    const queryParams = {
      expand: true,
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    const MAX_RETRIES = 2;
    const uploadWithRetry = async retriesLeft => {
      try {
        return await sdk.images.upload(bodyParams, queryParams);
      } catch (e) {
        if (retriesLeft > 0 && isNetworkError(e)) {
          await new Promise(resolve => setTimeout(resolve, 1000));
          return uploadWithRetry(retriesLeft - 1);
        }
        throw e;
      }
    };

    return uploadWithRetry(MAX_RETRIES)
      .then(resp => {
        const uploadedImage = resp.data.data;
        return { id, uploadedImage };
      })
      .catch(e => {
        const isNetError = isNetworkError(e);
        const logFn = isNetError ? log.warn : log.error;
        logFn(e, 'profile-image-upload-failed', {
          fileName: file?.name,
          fileSize: file?.size,
          fileType: file?.type,
          uploadId: id,
        });

        return rejectWithValue({ id, error: storableError(e) });
      });
  }
);
// Backward compatible wrapper for the uploadImage thunk
export const uploadImage = actionPayload => dispatch => {
  return dispatch(uploadImageThunk(actionPayload));
};

////////////////////
// Update Profile //
////////////////////
export const updateProfileThunk = createAsyncThunk(
  'ProfileSettingsPage/updateProfile',
  (actionPayload, { dispatch, rejectWithValue, extra: sdk }) => {
    const hasNameFields =
      Object.prototype.hasOwnProperty.call(actionPayload || {}, 'firstName') ||
      Object.prototype.hasOwnProperty.call(actionPayload || {}, 'lastName');
    const nameValidationIssue = hasNameFields
      ? validateSignupNameFields({
          firstName: actionPayload?.firstName,
          lastName: actionPayload?.lastName,
        })
      : null;

    if (nameValidationIssue) {
      log.warn(new Error('Suspicious profile name blocked.'), 'profile-name-blocked', {
        field: nameValidationIssue.field,
        code: nameValidationIssue.code,
      });
      return rejectWithValue(
        storableError(createSignupNameValidationApiError(nameValidationIssue))
      );
    }

    const queryParams = {
      expand: true,
      include: ['profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    const MAX_RETRIES = 2;
    const updateWithRetry = (payload, retriesLeft) => {
      return sdk.currentUser.updateProfile(payload, queryParams).catch(e => {
        const status = e?.status || e?.response?.status;
        const isConflict = status === StatusCode.CONFLICT;
        const isNetError = isNetworkError(e);
        if (isConflict && retriesLeft > 0) {
          // Wait a bit and retry to resolve the optimistic locking conflict
          return new Promise(resolve => setTimeout(resolve, 500)).then(() =>
            updateWithRetry(payload, retriesLeft - 1)
          );
        } else if (isNetError && retriesLeft > 0) {
          // Wait a bit and retry transient network error
          return new Promise(resolve => setTimeout(resolve, 1000)).then(() =>
            updateWithRetry(payload, retriesLeft - 1)
          );
        }
        throw e;
      });
    };

    return updateWithRetry(actionPayload, MAX_RETRIES)
      .then(response => {
        const entities = denormalisedResponseEntities(response);
        if (entities.length !== 1) {
          throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
        }
        const currentUser = entities[0];

        // Update current user in state.user.currentUser through user.duck.js
        dispatch(setCurrentUser(currentUser));
        return response;
      })
      .catch(e => {
        const status = e?.status || e?.response?.status;
        const isConflict = status === StatusCode.CONFLICT;
        const isNetError = isNetworkError(e);
        if (!isForbiddenError(e)) {
          const logFn = isConflict || isNetError ? log.warn : log.error;
          logFn(e, 'profile-update-failed', {
            fieldCount: Object.keys(actionPayload || {}).length,
            topLevelFields: Object.keys(actionPayload || {}).slice(0, 10),
            isConflict,
            isNetError,
          });
        }
        return rejectWithValue(storableError(e));
      });
  }
);
// Backward compatible wrapper for the updateProfile thunk
export const updateProfile = actionPayload => (dispatch, getState) => {
  if (!getState().auth.isAuthenticated) {
    return Promise.resolve();
  }
  return dispatch(updateProfileThunk(actionPayload));
};

////////////////////
// Check Username //
////////////////////
export const checkUsername = params => async () => {
  const normalizedUsername = normalizeUsername(params?.username);
  if (!normalizedUsername) {
    return { available: false, ownerId: null };
  }

  try {
    const response = await SharetribeApi.checkUsernameAvailability(normalizedUsername);
    const { available = false, ownerId = null } = response?.data || {};
    return { available, ownerId };
  } catch (e) {
    if (!isNetworkError(e)) {
      log.error(e, 'username-availability-check-failed', {
        usernameLength: normalizedUsername.length,
      });
    }
    throw storableError(e);
  }
};

// ================ Slice ================ //

const profileSettingsPageSlice = createSlice({
  name: 'ProfileSettingsPage',
  initialState: {
    image: null,
    uploadImageError: null,
    uploadInProgress: false,
    updateInProgress: false,
    updateProfileError: null,
  },
  reducers: {
    clearUpdatedForm: state => {
      state.updateProfileError = null;
      state.uploadImageError = null;
    },
  },
  extraReducers: builder => {
    builder
      // uploadImage cases
      .addCase(uploadImageThunk.pending, (state, action) => {
        const { id, file } = action.meta.arg;
        state.image = { id, file };
        state.uploadInProgress = true;
        state.uploadImageError = null;
      })
      .addCase(uploadImageThunk.fulfilled, (state, action) => {
        const { id, uploadedImage } = action.payload;
        const { file } = state.image || {};
        state.image = { id, imageId: uploadedImage.id, file, uploadedImage };
        state.uploadInProgress = false;
      })
      .addCase(uploadImageThunk.rejected, (state, action) => {
        state.image = null;
        state.uploadInProgress = false;
        state.uploadImageError = action.payload.error;
      })
      // updateProfile cases
      .addCase(updateProfileThunk.pending, state => {
        state.updateInProgress = true;
        state.updateProfileError = null;
      })
      .addCase(updateProfileThunk.fulfilled, state => {
        state.image = null;
        state.updateInProgress = false;
      })
      .addCase(updateProfileThunk.rejected, (state, action) => {
        state.image = null;
        state.updateInProgress = false;
        state.updateProfileError = action.payload;
      });
  },
});

export const { clearUpdatedForm } = profileSettingsPageSlice.actions;
export default profileSettingsPageSlice.reducer;
